import React from 'react';
import { Button, Collapse, Space, Steps, Tag, Typography } from 'antd';
import {
  AuditOutlined,
  CheckCircleOutlined,
  CloudUploadOutlined,
  EditOutlined,
  LinkOutlined,
  PlayCircleOutlined,
  PlusCircleOutlined,
  ReloadOutlined,
  SafetyCertificateOutlined,
} from '@ant-design/icons';
import type {
  ActivationDecision,
  AdminModelMeta,
  ModelCandidatePackage,
  ModelWorkflowProjection,
} from '../types';

const { Text } = Typography;

const QUALIFIED_STATUSES = new Set(['platform_qualified', 'clinical_validated']);
const FAILED_PACKAGE_STATUSES = new Set(['package_invalid', 'script_failed', 'source_rejected']);

export type ModelPackageLifecycleAction =
  | 'associate'
  | 'create_draft'
  | 'edit_model'
  | 'qualify'
  | 'review'
  | 'publish'
  | 'activate'
  | 'override'
  | 'refresh'
  | 'view';

export interface ModelPackageLifecycleInput {
  candidate: ModelCandidatePackage;
  meta?: AdminModelMeta;
  expectedModelExists?: boolean;
  isLatestCandidate?: boolean;
  activationDecision?: ActivationDecision;
  currentReleaseActive?: boolean;
  draftMissingItems?: string[];
  workflow?: ModelWorkflowProjection | null;
}

export interface ModelPackageLifecycleState {
  current: number;
  status: 'process' | 'error' | 'finish';
  title: string;
  description: React.ReactNode;
  action?: ModelPackageLifecycleAction;
  actionLabel?: string;
  actionAriaLabel?: string;
  actionDisabled?: boolean;
  route: 'new_model' | 'script_upgrade' | 'historical';
  items: Array<{
    title: string;
    description?: string;
    status?: 'wait' | 'process' | 'finish' | 'error';
  }>;
}

const publishedQualificationValue = (meta: AdminModelMeta | undefined, key: string) => {
  const value = meta?.published_qualification?.[key];
  return typeof value === 'string' ? value : undefined;
};

export const isCurrentCandidatePublished = (
  candidate: ModelCandidatePackage,
  meta?: AdminModelMeta,
) => Boolean(
  meta?.published_qualification
  && meta.published_qualification.package_id === candidate.id
  && meta.published_qualification.package_sha256 === candidate.package_sha256,
);

export const isCurrentCandidateActive = (
  candidate: ModelCandidatePackage,
  meta?: AdminModelMeta,
) => {
  if (!meta || meta.model_status !== 'active' || !isCurrentCandidatePublished(candidate, meta)) {
    return false;
  }
  const releaseId = publishedQualificationValue(meta, 'release_id');
  if (releaseId && meta.active_release_id) return releaseId === meta.active_release_id;
  return Boolean(
    meta.script_file_base
    && meta.script_file_base === meta.published_qualification?.script_file_base,
  );
};

const actionIcon = (action?: ModelPackageLifecycleAction) => {
  if (action === 'associate') return <LinkOutlined />;
  if (action === 'create_draft') return <PlusCircleOutlined />;
  if (action === 'edit_model') return <EditOutlined />;
  if (action === 'qualify') return <PlayCircleOutlined />;
  if (action === 'review') return <AuditOutlined />;
  if (action === 'publish') return <CloudUploadOutlined />;
  if (action === 'activate') return <CheckCircleOutlined />;
  if (action === 'override') return <SafetyCertificateOutlined />;
  return <ReloadOutlined />;
};

export const resolveModelPackageLifecycle = ({
  candidate,
  meta,
  expectedModelExists = false,
  isLatestCandidate = true,
  activationDecision,
  currentReleaseActive,
  draftMissingItems = [],
  workflow,
}: ModelPackageLifecycleInput): ModelPackageLifecycleState => {
  const withdrawn = ['withdrawn', 'archived'].includes(candidate.status);
  const running = candidate.status === 'qualification_running';
  const qualified = QUALIFIED_STATUSES.has(candidate.status);
  const failed = FAILED_PACKAGE_STATUSES.has(candidate.status);
  const published = isCurrentCandidatePublished(candidate, meta);
  const active = currentReleaseActive ?? isCurrentCandidateActive(candidate, meta);
  const hasReleasedBaseline = Boolean(
    meta?.published_qualification || meta?.active_release_id || meta?.model_status === 'active',
  );
  const route: ModelPackageLifecycleState['route'] = !isLatestCandidate
    ? 'historical'
    : hasReleasedBaseline ? 'script_upgrade' : 'new_model';
  const registrationLabel = route === 'script_upgrade' ? '沿用模型资料' : '注册模型资料';
  const steps: ModelPackageLifecycleState['items'] = [
    { title: '模型版本', status: 'finish' },
    { title: registrationLabel, status: 'wait' },
    { title: '技术资格', status: 'wait' },
    { title: '版本审核', status: 'wait' },
    { title: '发布入库', status: 'wait' },
    { title: '运行版本', status: 'wait' },
  ];
  const finishThrough = (index: number) => {
    steps.forEach((step, stepIndex) => {
      if (stepIndex <= index) step.status = 'finish';
    });
  };

  if (workflow && candidate.model_id === workflow.model_id && isLatestCandidate) {
    const stageIndex: Record<string, number> = {
      registration: 1,
      package: 1,
      qualification: 2,
      review: 3,
      publication: 4,
      activation: 5,
      complete: 5,
    };
    const current = stageIndex[workflow.stage] ?? 1;
    const blocker = workflow.blockers[0]?.message;
    const action = workflow.next_action as ModelPackageLifecycleAction | null | undefined;
    const labels: Record<string, string> = {
      edit_model: '继续完善并提交',
      upload_package: '上传模型包',
      qualify: '开始资格验证',
      refresh: '刷新状态',
      review: '审核版本',
      publish: '发布到模型库',
      activate: meta?.model_status === 'active' ? '切换到此版本' : '批准全局测试',
    };
    const statuses: ModelPackageLifecycleState['items'] = steps.map((step, index) => ({
      ...step,
      status: index < current ? 'finish' : index === current ? (workflow.stage === 'complete' ? 'finish' : 'process') : 'wait',
    }));
    return {
      current,
      status: workflow.stage === 'complete' ? 'finish' : workflow.blockers.length ? 'process' : 'finish',
      title: workflow.stage === 'complete' ? '当前版本已发布并生效' : blocker || '模型流程待推进',
      description: blocker || '按当前版本流程继续处理。',
      action: action || undefined,
      actionLabel: action ? labels[action] || action : undefined,
      actionAriaLabel: action || undefined,
      route,
      items: statuses,
    };
  }

  if (withdrawn) {
    steps[0].status = 'error';
    return {
      current: 0,
      status: 'error',
      title: '模型版本已撤回',
      description: '验证和发布证据继续保留用于审计，但该版本不能再次推进。',
      route,
      items: steps,
    };
  }

  if (!isLatestCandidate) {
    if (candidate.model_id) steps[1].status = 'finish';
    if (qualified) steps[2].status = 'finish';
    if (published) steps[4].status = 'finish';
    if (active) steps[5].status = 'finish';
    return {
      current: active ? 5 : published ? 4 : qualified ? 2 : 0,
      status: 'finish',
      title: active ? '当前运行版本' : published ? '已发布历史版本' : '历史模型版本',
      description: '历史版本不再从列表重复推进；可在详情的版本历史中查看、切换或回滚。',
      action: 'view',
      actionLabel: '查看版本',
      actionAriaLabel: 'View version workflow',
      route: 'historical',
      items: steps,
    };
  }

  if (!candidate.model_id) {
    steps[1].status = 'process';
    return {
      current: 1,
      status: 'process',
      title: expectedModelExists ? '关联已有模型' : '创建模型草稿',
      description: expectedModelExists
        ? 'manifest 中的 model_id 已存在，可直接关联并沿用模型资料。'
        : '系统将从 manifest 建立模型草稿，随后补齐并提交模型资料。',
      action: expectedModelExists ? 'associate' : 'create_draft',
      actionLabel: expectedModelExists ? '关联已有模型' : '创建模型草稿',
      actionAriaLabel: expectedModelExists ? 'Associate model' : 'Create model draft',
      route,
      items: steps,
    };
  }

  if (!meta) {
    steps[1].status = 'error';
    return {
      current: 1,
      status: 'error',
      title: '模型信息暂未加载',
      description: '流程操作已暂停，刷新模型元数据后再继续。',
      action: 'refresh',
      actionLabel: '刷新状态',
      actionAriaLabel: 'Refresh lifecycle',
      route,
      items: steps,
    };
  }

  if (meta.draft_mode) {
    steps[1].status = 'process';
    const missing = draftMissingItems.length
      ? ` 当前提交门槛尚缺：${draftMissingItems.join('、')}。`
      : ' 必填项已齐，可进入向导核对并提交。';
    return {
      current: 1,
      status: 'process',
      title: '完善并提交模型资料',
      description: `向导进度第 ${Math.min(Math.max(meta.last_edited_step || 1, 1), 7)} / 7 步。${missing}`,
      action: 'edit_model',
      actionLabel: '继续完善并提交',
      actionAriaLabel: 'Edit model',
      route,
      items: steps,
    };
  }

  finishThrough(1);
  if (running) {
    steps[2].status = 'process';
    steps[2].description = '运行中';
    return {
      current: 2,
      status: 'process',
      title: '技术资格验证正在运行',
      description: '完成后系统会刷新当前模型版本的执行与一致性证据。',
      action: 'refresh',
      actionLabel: '刷新运行状态',
      actionAriaLabel: 'Qualification running',
      route,
      items: steps,
    };
  }

  if (!qualified) {
    steps[2].status = failed ? 'error' : 'process';
    return {
      current: 2,
      status: failed ? 'error' : 'process',
      title: failed ? '技术资格未通过' : '运行技术资格验证',
      description: failed
        ? '请修正脚本或模型包后重新验证；当前运行版本不受该失败版本影响。'
        : '验证会实际加载 R、编译 C++、执行支持方法，并检查标准场景双引擎一致性。',
      action: 'qualify',
      actionLabel: failed ? '重新运行验证' : '开始资格验证',
      actionAriaLabel: failed ? 'Rerun qualification' : 'Start qualification',
      route,
      items: steps,
    };
  }

  finishThrough(2);
  if (meta.review_status !== 'approved') {
    steps[3].status = meta.review_status === 'rejected' ? 'error' : 'process';
    return {
      current: 3,
      status: meta.review_status === 'rejected' ? 'error' : 'process',
      title: meta.review_status === 'rejected' ? '版本审核已驳回' : '审核当前模型版本',
      description: meta.review_note || '技术资格已通过；审核确认本次模型资料或脚本变更后即可发布。',
      action: 'review',
      actionLabel: meta.review_status === 'rejected' ? '重新审核' : '审核版本',
      actionAriaLabel: 'Review model',
      route,
      items: steps,
    };
  }

  finishThrough(3);
  if (!published) {
    steps[4].status = 'process';
    return {
      current: 4,
      status: 'process',
      title: '发布当前版本到模型库',
      description: '发布会生成不可变版本记录，但不会自动改变当前运行版本。',
      action: 'publish',
      actionLabel: '发布到模型库',
      actionAriaLabel: 'Publish model',
      route,
      items: steps,
    };
  }

  finishThrough(4);
  if (!active) {
    const overrideEligible = Boolean(
      activationDecision
      && !activationDecision.allowed
      && ['qualification_required', 'new_model_qualification_required'].includes(
        activationDecision.reason_code,
      ),
    );
    steps[5].status = activationDecision && !activationDecision.allowed ? 'error' : 'process';
    return {
      current: 5,
      status: activationDecision && !activationDecision.allowed ? 'error' : 'process',
      title: meta.model_status === 'active' ? '切换到新发布版本' : '批准全局测试',
      description: activationDecision?.message
        || (meta.model_status === 'active'
          ? '旧版本仍在运行；切换后新版本将原子替换当前运行脚本。'
          : '版本已入库；管理员批准后参与全局匹配。'),
      action: overrideEligible ? 'override' : 'activate',
      actionLabel: overrideEligible
        ? '审核覆盖激活'
        : meta.model_status === 'active' ? '切换到此版本' : '批准全局测试',
      actionAriaLabel: overrideEligible ? 'Request activation override' : 'Activate model',
      actionDisabled: Boolean(activationDecision && !activationDecision.allowed && !overrideEligible),
      route,
      items: steps,
    };
  }

  finishThrough(5);
  steps[5].description = '当前运行';
  return {
    current: 5,
    status: 'finish',
    title: '当前版本已发布并生效',
    description: '后续脚本迭代可直接使用“脚本快速升级”，无需重新登记模型资料。',
    route,
    items: steps,
  };
};

export interface ModelPackageLifecycleProps extends ModelPackageLifecycleInput {
  loading?: boolean;
  compact?: boolean;
  onAction?: (action: ModelPackageLifecycleAction) => void;
}

const ModelPackageLifecycle: React.FC<ModelPackageLifecycleProps> = ({
  loading = false,
  compact = false,
  onAction,
  ...input
}) => {
  const state = resolveModelPackageLifecycle(input);
  const routeLabel = state.route === 'script_upgrade'
    ? '已有模型脚本升级'
    : state.route === 'historical' ? '历史版本' : '新模型入库';
  const action = state.action && onAction ? (
    <Button
      type={state.action === 'view' || state.actionDisabled ? 'default' : 'primary'}
      icon={actionIcon(state.action)}
      aria-label={state.actionAriaLabel}
      loading={loading}
      disabled={state.actionDisabled}
      onClick={() => onAction(state.action!)}
    >
      {state.actionLabel}
    </Button>
  ) : undefined;

  if (compact) {
    return (
      <Space direction="vertical" size={4} style={{ width: '100%' }}>
        <Space size={6} wrap>
          <Tag color={state.route === 'script_upgrade' ? 'cyan' : state.route === 'historical' ? 'default' : 'blue'}>
            {routeLabel}
          </Tag>
          <Text strong>{state.title}</Text>
        </Space>
        {action}
      </Space>
    );
  }

  return (
    <div className={`model-workflow-summary model-workflow-summary--${state.status}`}>
      <div className="model-workflow-summary__main">
        <div className="model-workflow-summary__copy">
          <Space size={8} wrap>
            <Tag color={state.status === 'error' ? 'red' : state.status === 'finish' ? 'green' : 'blue'}>
              {state.status === 'finish' ? '已完成' : `当前阶段 ${state.current + 1}/6`}
            </Tag>
            <Tag>{routeLabel}</Tag>
          </Space>
          <Text strong className="model-workflow-summary__title">{state.title}</Text>
          <Text type="secondary" className="model-workflow-summary__description">
            {state.description}
          </Text>
        </div>
        {action ? <div className="model-workflow-summary__action">{action}</div> : null}
      </div>
      <Collapse
        ghost
        size="small"
        className="model-workflow-summary__details"
        items={[{
          key: 'workflow',
          label: '查看完整流程',
          children: (
            <Steps
              size="small"
              current={state.current}
              status={state.status === 'finish' ? 'finish' : state.status}
              responsive
              items={state.items}
            />
          ),
        }]}
      />
    </div>
  );
};

export default ModelPackageLifecycle;
